/** QuickHouse — Arborescence du contenu : étages → dossiers → éléments (hiérarchie parent/enfant).
 *  Clic = sélectionner, œil = masquer/afficher, double-clic = renommer, +📁 = dossier.
 *  GLISSER-DÉPOSER : déposer un élément SUR un autre en fait un enfant (il garde ses
 *  coordonnées absolues ; déplacer le parent déplacera ensuite toute sa descendance).
 *  Déposer sur un dossier = ranger dedans ; sur l'étage = détacher (racine). */
import { useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { wallLength, fmtLen } from '../../shared/geometry.js';
import { newId } from '../../shared/schema.js';

const TYPE_FILTERS = {
  '': 'Tous les types',
  wall: 'Murs',
  opening: 'Ouvertures',
  room: 'Pièces',
  slab: 'Dalles',
  roof: 'Toits',
  stair: 'Escaliers',
  item: 'Objets',
  beam: 'Charpente',
  dim: 'Cotes',
};

export default function OutlinePanel() {
  const project = useProjectStore((s) => s.project);
  const activeFloorId = useProjectStore((s) => s.activeFloorId);
  const [collapsed, setCollapsed] = useState({});
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  if (!project) return null;

  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  const floors = [...project.floors].sort((p, q) => p.level - q.level);
  const filtering = query.trim() !== '' || typeFilter !== '';
  const matches = (it) => (typeFilter === '' || it.type === typeFilter)
    && (query.trim() === '' || it.label.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className="outline">
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        <input
          placeholder="Filtrer par nom…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, padding: '3px 6px', fontSize: 11 }}
        />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ fontSize: 11 }}>
          {Object.entries(TYPE_FILTERS).map(([v, label]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </select>
      </div>
      {floors.map((floor) => (
        <FloorNode
          key={floor.id}
          floor={floor}
          active={floor.id === activeFloorId}
          collapsed={collapsed}
          toggle={toggle}
          filtering={filtering}
          matches={matches}
        />
      ))}
    </div>
  );
}

// ── Drag & drop ───────────────────────────────────────────────
function dragData(e) {
  try { return JSON.parse(e.dataTransfer.getData('text/plain')); } catch { return null; }
}

/** Rattache `child` à `newParentId` (élément) ou `folderId` — en conservant ses coordonnées absolues */
function reparent(mutate, setStatus, floor, childId, { parentId = null, folderId = null }) {
  if (childId === parentId) return;
  // Anti-cycle : parentId ne doit pas être un descendant de childId
  if (parentId) {
    const all = [...floor.walls, ...floor.openings, ...floor.rooms, ...(floor.stairs ?? []), ...(floor.items ?? [])];
    let cur = parentId;
    for (let i = 0; i < 100 && cur; i++) {
      if (cur === childId) { setStatus('Impossible : cela créerait un cycle parent/enfant'); return; }
      cur = all.find((x) => x.id === cur)?.parentId ?? null;
    }
  }
  mutate((floors) => {
    const f = floors.find((x) => x.id === floor.id);
    for (const list of [f.walls, f.openings, f.rooms, f.stairs ?? [], f.items ?? []]) {
      const it = list.find((x) => x.id === childId);
      if (it) {
        it.parentId = parentId;
        it.folderId = folderId;
      }
    }
  });
  setStatus(parentId ? 'Élément rattaché — déplacer le parent déplacera l\'enfant' : '');
}

function useDropTarget(onDrop) {
  const [over, setOver] = useState(false);
  return {
    over,
    props: {
      onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); setOver(true); },
      onDragLeave: () => setOver(false),
      onDrop: (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        const data = dragData(e);
        if (data) onDrop(data);
      },
    },
  };
}

// ── Nœuds ─────────────────────────────────────────────────────
function FloorNode({ floor, active, collapsed, toggle, filtering = false, matches = () => true }) {
  const mutate = useProjectStore((s) => s.mutate);
  const setStatus = useProjectStore((s) => s.setStatus);
  const setActiveFloor = useProjectStore((s) => s.setActiveFloor);
  const folded = collapsed[floor.id];

  const folders = floor.folders ?? [];
  const items = itemsOf(floor);
  const ids = new Set(items.map((it) => it.id));
  const isRoot = (it) => (!it.parentId || !ids.has(it.parentId))
    && (!it.folderId || !folders.some((f) => f.id === it.folderId));
  const rootItems = items.filter(isRoot);

  const addFolder = (e) => {
    e.stopPropagation();
    const name = window.prompt('Nom du dossier ?', 'Dossier');
    if (!name?.trim()) return;
    mutate((floors) => {
      const f = floors.find((x) => x.id === floor.id);
      if (!f.folders) f.folders = [];
      f.folders.push({ id: newId('d'), name: name.trim(), parentId: null });
    });
  };

  // Déposer sur l'étage = détacher (racine, hors dossier)
  const drop = useDropTarget((data) => reparent(mutate, setStatus, floor, data.id, {}));

  return (
    <div>
      <div
        className={`node floor-node ${active ? 'active' : ''} ${drop.over ? 'drop-over' : ''}`}
        onClick={() => setActiveFloor(floor.id)}
        {...drop.props}
      >
        <span className="caret" onClick={(e) => { e.stopPropagation(); toggle(floor.id); }}>
          {folded ? '▸' : '▾'}
        </span>
        <span className="grow">🏠 {floor.name}</span>
        <button className="mini" title="Nouveau dossier" onClick={addFolder}>+📁</button>
      </div>
      {!folded && filtering && (
        // Mode filtre : liste plate des correspondances de l'étage
        <div className="children">
          {items.filter(matches).map((it) => (
            <ItemNode key={it.key} floor={floor} item={it} items={[]} depth={0}
              collapsed={collapsed} toggle={toggle} />
          ))}
          {!items.some(matches) && <div className="empty">— aucun résultat —</div>}
        </div>
      )}
      {!folded && !filtering && (
        <div className="children">
          {folders.map((folder) => (
            <FolderNode key={folder.id} floor={floor} folder={folder} items={items}
              collapsed={collapsed} toggle={toggle} />
          ))}
          {rootItems.map((it) => (
            <ItemNode key={it.key} floor={floor} item={it} items={items} depth={0}
              collapsed={collapsed} toggle={toggle} />
          ))}
          {!folders.length && !rootItems.length && <div className="empty">— vide —</div>}
        </div>
      )}
    </div>
  );
}

function FolderNode({ floor, folder, items, collapsed, toggle }) {
  const mutate = useProjectStore((s) => s.mutate);
  const setStatus = useProjectStore((s) => s.setStatus);
  const selection = useProjectStore((s) => s.selection);
  const folded = collapsed[folder.id];
  const ids = new Set(items.map((it) => it.id));
  // Contenu du dossier : éléments rangés dedans et sans parent valide
  const inFolder = items.filter((it) => it.folderId === folder.id && (!it.parentId || !ids.has(it.parentId)));

  const rename = () => {
    const name = window.prompt('Nom du dossier :', folder.name);
    if (!name?.trim()) return;
    mutate((floors) => {
      const d = floors.find((f) => f.id === floor.id).folders.find((x) => x.id === folder.id);
      if (d) d.name = name.trim();
    });
  };

  const remove = (e) => {
    e.stopPropagation();
    mutate((floors) => {
      const f = floors.find((x) => x.id === floor.id);
      f.folders = f.folders.filter((d) => d.id !== folder.id);
      for (const list of [f.walls, f.openings, f.rooms, f.stairs ?? [], f.items ?? []]) {
        for (const it of list) if (it.folderId === folder.id) it.folderId = null;
      }
    });
  };

  const moveHere = (e) => {
    e.stopPropagation();
    for (const sel of selection) {
      reparent(mutate, setStatus, floor, sel.id, { folderId: folder.id });
    }
  };

  const drop = useDropTarget((data) => reparent(mutate, setStatus, floor, data.id, { folderId: folder.id }));

  return (
    <div>
      <div className={`node folder-node ${drop.over ? 'drop-over' : ''}`} onDoubleClick={rename} {...drop.props}>
        <span className="caret" onClick={() => toggle(folder.id)}>{folded ? '▸' : '▾'}</span>
        <span className="grow">📁 {folder.name} <span className="count">({inFolder.length})</span></span>
        {selection.length > 0 && (
          <button className="mini" title="Déplacer la sélection dans ce dossier" onClick={moveHere}>⤵</button>
        )}
        <button className="mini danger" title="Supprimer le dossier (les éléments sont conservés)" onClick={remove}>✕</button>
      </div>
      {!folded && (
        <div className="children">
          {inFolder.map((it) => (
            <ItemNode key={it.key} floor={floor} item={it} items={items} depth={0}
              collapsed={collapsed} toggle={toggle} />
          ))}
          {!inFolder.length && <div className="empty">— vide —</div>}
        </div>
      )}
    </div>
  );
}

function ItemNode({ floor, item, items, depth, collapsed = {}, toggle = () => {} }) {
  const mutate = useProjectStore((s) => s.mutate);
  const setStatus = useProjectStore((s) => s.setStatus);
  const selection = useProjectStore((s) => s.selection);
  const setSelection = useProjectStore((s) => s.setSelection);
  const setActiveFloor = useProjectStore((s) => s.setActiveFloor);
  const activeFloorId = useProjectStore((s) => s.activeFloorId);
  const selected = selection.some((s) => s.kind === item.kind && s.id === item.id);
  const children = depth < 12 ? items.filter((it) => it.parentId === item.id) : [];
  const folded = collapsed[item.id];

  const select = (e) => {
    if (activeFloorId !== floor.id) setActiveFloor(floor.id);
    if (e.ctrlKey || e.metaKey) {
      setSelection(selected
        ? selection.filter((s) => !(s.kind === item.kind && s.id === item.id))
        : [...selection, { kind: item.kind, id: item.id }]);
    } else {
      setSelection([{ kind: item.kind, id: item.id }]);
    }
  };

  const toggleEye = (e) => {
    e.stopPropagation();
    mutate((floors) => {
      const f = floors.find((x) => x.id === floor.id);
      const it = listFor(f, item.kind).find((x) => x.id === item.id);
      if (it) it.hidden = !it.hidden;
    });
  };

  const rename = (e) => {
    e.stopPropagation();
    if (item.kind !== 'room') return;
    const name = window.prompt('Nom :', item.label);
    if (!name?.trim()) return;
    mutate((floors) => {
      const r = floors.find((x) => x.id === floor.id).rooms.find((x) => x.id === item.id);
      if (r) r.name = name.trim();
    });
  };

  // Déposer SUR cet élément = en faire le parent (coordonnées absolues conservées)
  const drop = useDropTarget((data) => {
    if (data.id === item.id) return;
    reparent(mutate, setStatus, floor, data.id, { parentId: item.id });
  });

  // Dalle : rattacher tous les murs de l'étage en un clic (ils reposent alors SUR la dalle)
  const attachAllWalls = (e) => {
    e.stopPropagation();
    let count = 0;
    mutate((floors) => {
      const f = floors.find((x) => x.id === floor.id);
      for (const w of f.walls) {
        if (w.parentId !== item.id) { w.parentId = item.id; count++; }
      }
    });
    setStatus(count
      ? `${count} mur(s) posé(s) sur « ${item.label} » — ils reposent sur la dalle et la suivent`
      : 'Tous les murs sont déjà posés sur cette dalle');
  };

  return (
    <div>
      <div
        className={`node item-node ${selected ? 'selected' : ''} ${item.hidden ? 'hidden-item' : ''} ${drop.over ? 'drop-over' : ''}`}
        onClick={select}
        onDoubleClick={rename}
        draggable={item.draggable}
        onDragStart={(e) => {
          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: item.kind, id: item.id }));
          e.dataTransfer.effectAllowed = 'move';
        }}
        title={item.draggable
          ? 'Glissez sur un autre élément pour l\'y rattacher (parent/enfant)'
          : 'Suit son mur'}
        {...drop.props}
      >
        {children.length > 0 && (
          <span className="caret" onClick={(e) => { e.stopPropagation(); toggle(item.id); }}>
            {folded ? '▸' : '▾'}
          </span>
        )}
        <span className="grow">{item.icon} {item.label}</span>
        {item.isSlab && (
          <button className="mini" title="Poser tous les murs de l'étage sur cette dalle (parent/enfant : ils reposent dessus et la suivent)"
            onClick={attachAllWalls}>
            🧱⬆
          </button>
        )}
        <span className="mini eye-btn" onClick={toggleEye} title={item.hidden ? 'Afficher' : 'Masquer'}>
          {item.hidden ? '🚫' : '👁'}
        </span>
      </div>
      {children.length > 0 && !folded && (
        <div className="children">
          {children.map((c) => (
            <ItemNode key={c.key} floor={floor} item={c} items={items} depth={depth + 1}
              collapsed={collapsed} toggle={toggle} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────
function listFor(floor, kind) {
  if (kind === 'wall') return floor.walls;
  if (kind === 'opening') return floor.openings;
  if (kind === 'stair') return floor.stairs ?? [];
  if (kind === 'item') return floor.items ?? [];
  if (kind === 'dim') return floor.dims ?? [];
  if (kind === 'beam') return floor.beams ?? [];
  return floor.rooms;
}

const ITEM_ICONS = { elec: '⚡', plomb: '🚰', meuble: '🪑' };

const OPENING_ICONS = { door: '🚪', window: '⊞', garage: '🚗', passage: '▢' };
const STAIR_LABELS = { straight: 'Escalier', ladder: 'Échelle de meunier', spiral: 'Colimaçon' };

function itemsOf(floor) {
  const out = [];
  for (const r of floor.rooms) {
    const isSlab = r.kind === 'slab';
    const isRoof = r.kind === 'roof';
    out.push({
      kind: 'room', id: r.id, key: r.id, folderId: r.folderId, parentId: r.parentId, hidden: r.hidden,
      type: isSlab ? 'slab' : isRoof ? 'roof' : 'room',
      icon: isSlab ? '▦' : isRoof ? '⛰' : '◻', draggable: true, isSlab,
      label: isSlab ? `${r.name}${(r.elevation ?? 0) > 0 ? ' (mezzanine)' : ''}`
        : isRoof ? `${r.name} (toit)` : r.name,
    });
  }
  for (const w of floor.walls) {
    out.push({
      kind: 'wall', id: w.id, key: w.id, folderId: w.folderId, parentId: w.parentId, hidden: w.hidden, type: 'wall',
      icon: '─', draggable: true, label: `Mur ${fmtLen(wallLength(w))} cm`,
    });
  }
  for (const o of floor.openings) {
    const name = o.type === 'door' ? 'Porte'
      : o.type === 'garage' ? 'Garage'
        : o.type === 'passage' ? 'Ouverture'
          : (o.variant === 'velux' ? 'Velux' : 'Fenêtre');
    const attached = !!o.wallId;
    out.push({
      kind: 'opening', id: o.id, key: o.id, folderId: o.folderId, type: 'opening',
      // Une ouverture sur mur suit déjà son mur : affichée comme enfant du mur, non déplaçable
      parentId: attached ? o.wallId : o.parentId,
      hidden: o.hidden,
      icon: o.variant === 'velux' ? '▨' : OPENING_ICONS[o.type] ?? '⊞',
      draggable: !attached,
      label: `${name} ${o.width} cm`,
    });
  }
  for (const s of floor.stairs ?? []) {
    out.push({
      kind: 'stair', id: s.id, key: s.id, folderId: s.folderId, parentId: s.parentId, hidden: s.hidden, type: 'stair',
      icon: s.kind === 'spiral' ? '🌀' : '🪜', draggable: true,
      label: STAIR_LABELS[s.kind] ?? 'Escalier',
    });
  }
  for (const b of floor.beams ?? []) {
    out.push({
      kind: 'beam', id: b.id, key: b.id, folderId: b.folderId, parentId: b.parentId, hidden: b.hidden,
      type: 'beam', icon: b.kind === 'post' ? '▮' : '🪵', draggable: true,
      label: b.kind === 'post'
        ? `Poteau ${fmtLen(b.w)} × ${fmtLen(b.d ?? b.w)} cm`
        : `Poutre ${fmtLen(Math.hypot(b.b.x - b.a.x, b.b.y - b.a.y))} cm`,
    });
  }
  for (const d of floor.dims ?? []) {
    out.push({
      kind: 'dim', id: d.id, key: d.id, folderId: d.folderId, parentId: d.parentId, hidden: d.hidden,
      type: 'dim', icon: '📏', draggable: true,
      label: `Cote ${fmtLen(Math.hypot(d.b.x - d.a.x, d.b.y - d.a.y))} cm`,
    });
  }
  const catalog = useProjectStore.getState().catalog ?? [];
  for (const it of floor.items ?? []) {
    const entry = catalog.find((e) => e.id === it.catalogId);
    out.push({
      kind: 'item', id: it.id, key: it.id, folderId: it.folderId, parentId: it.parentId, hidden: it.hidden, type: 'item',
      icon: ITEM_ICONS[entry?.category] ?? '📦', draggable: true,
      label: it.label || entry?.name || it.catalogId,
    });
  }
  return out;
}
